import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import {
  CheckOutlined,
  CopyOutlined,
  HistoryOutlined,
  SendOutlined,
  WhatsAppOutlined,
} from '@ant-design/icons';
import { useAskArchive } from '../hooks/useAskArchive';
import HistorianAnswerDisplay from '../components/HistorianAnswerDisplay';
import './AskArchive.css';

const exampleQuestions = [
  'Who has won the most championships?',
  'Show me the 2025 final standings.',
  'What is The Mojos all-time record?',
  'Who has been traded the most?',
];

const copyToClipboard = async (text: string) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  document.execCommand('copy');
  document.body.removeChild(textarea);
};

const AskArchive: React.FC = () => {
  const [question, setQuestion] = useState('');
  const [copied, setCopied] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);
  const { ask, answer, error, isLoading, reset } = useAskArchive();

  const trimmedQuestion = question.trim();
  const canSubmit = trimmedQuestion.length >= 8 && !isLoading;

  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'GL Historian | Gibsons League';

    return () => {
      document.title = previousTitle;
    };
  }, []);

  const sharePayload = useMemo(() => {
    if (!answer) {
      return null;
    }

    const shareUrl = answer.shareUrl || `${window.location.origin}/ask/`;
    const brandedText = answer.shareText?.trim() || `📚 GL Historian\n${answer.answer}`;
    const text = `${brandedText}\n\n${shareUrl}`;

    return {
      text,
      whatsAppUrl: `https://wa.me/?text=${encodeURIComponent(text)}`,
    };
  }, [answer]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!canSubmit) {
      return;
    }

    setCopied(false);
    setShareError(null);
    try {
      await ask(trimmedQuestion);
    } catch {
      // The hook owns the user-facing error state.
    }
  };

  const handleCopy = async () => {
    if (!sharePayload) {
      return;
    }

    setShareError(null);
    try {
      await copyToClipboard(sharePayload.text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2400);
    } catch {
      setShareError('Copying is not available in this browser. Select and copy the answer instead.');
    }
  };

  const handleAskAnother = () => {
    reset();
    setQuestion('');
    setCopied(false);
    setShareError(null);
  };

  return (
    <div className="ask-archive-page">
      <section className="ask-hero" aria-labelledby="ask-archive-title">
        <img
          className="ask-historian-logo"
          src="/gl-historian-logo.png"
          alt="GL Historian emblem"
        />
        <div className="ask-eyebrow"><HistoryOutlined /> Gibsons League intelligence</div>
        <h1 id="ask-archive-title">GL Historian</h1>
        <p>
          Ask the Gibsons League historian about seasons, franchises, records, and trades.
          Every answer is grounded in the published archive.
        </p>
      </section>

      <section className="ask-panel">
        <form onSubmit={handleSubmit} className="ask-form">
          <label htmlFor="archive-question">What do you want to know?</label>
          <textarea
            id="archive-question"
            value={question}
            onChange={(event) => setQuestion(event.target.value)}
            placeholder="For example: Who has the best regular-season win percentage?"
            maxLength={500}
            rows={4}
            disabled={isLoading}
          />
          <div className="ask-form-footer">
            <span className="ask-character-count">{question.length}/500</span>
            <button className="ask-submit" type="submit" disabled={!canSubmit}>
              <SendOutlined /> {isLoading ? 'Searching the archive…' : 'Ask the archive'}
            </button>
          </div>
        </form>

        {!answer && !isLoading && (
          <div className="ask-examples" aria-label="Example questions">
            <span>Try asking</span>
            <div className="ask-example-list">
              {exampleQuestions.map((example) => (
                <button type="button" key={example} onClick={() => setQuestion(example)}>
                  {example}
                </button>
              ))}
            </div>
          </div>
        )}

        {isLoading && (
          <div className="ask-loading" role="status" aria-live="polite">
            <span className="ask-loading-mark" aria-hidden="true" />
            <div>
              <strong>Checking the record book</strong>
              <span>Finding the relevant seasons and evidence…</span>
            </div>
          </div>
        )}

        {error && (
          <div className="ask-error" role="alert">
            <strong>That search did not work.</strong>
            <span>{error}</span>
          </div>
        )}
      </section>

      {answer && (
        <article className="archive-answer" aria-live="polite">
          <header>
            <div>
              <span className="answer-kicker">FantasyArchive found</span>
              <h2>{answer.question}</h2>
            </div>
            {answer.cached && <span className="answer-cache-label">Quick replay</span>}
          </header>

          <HistorianAnswerDisplay answer={answer.answer} presentation={answer.presentation} />

          {answer.evidence.length > 0 && (
            <section className="answer-evidence" aria-labelledby="answer-evidence-title">
              <h3 id="answer-evidence-title">Evidence</h3>
              <ul>
                {answer.evidence.map((item, index) => <li key={`${item}-${index}`}>{item}</li>)}
              </ul>
            </section>
          )}

          <footer>
            <div className="answer-actions">
              {sharePayload && (
                <a
                  className="answer-share"
                  href={sharePayload.whatsAppUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <WhatsAppOutlined /> Share to WhatsApp
                </a>
              )}
              <button type="button" className="answer-copy-button" onClick={handleCopy}>
                {copied ? <CheckOutlined /> : <CopyOutlined />}
                {copied ? 'Copied' : 'Copy'}
              </button>
              <button type="button" className="answer-reset" onClick={handleAskAnother}>
                Ask another
              </button>
            </div>
            <span className="answer-version">
              Agent: {answer.agentVersion} · Archive data: {answer.dataVersion}
            </span>
          </footer>

          {shareError && <p className="share-error" role="alert">{shareError}</p>}
        </article>
      )}

      <aside className="ask-scope-note">
        <strong>What it knows today</strong>
        <span>
          Published standings, franchise histories, record-book categories, and trade summaries.
          It will say when a question needs data that has not been exported or a deeper MCP-enabled analysis.
        </span>
      </aside>
    </div>
  );
};

export default AskArchive;
